import { watch, onUnmounted } from 'vue'
import {
  OrderRequest,
  OrderTicket,
  OrdersHubService,
  Store,
  type IOrderTicket,
} from 'app-core'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/useAppStore'
import { useOrdersHub } from '@/composables/useOrdersHub'
import { useToast } from '@/composables/useToast'
import { showAlert } from '@/composables/useAlert'
import { emitter } from '@/utilities/emitter'
import { markOrderRequestAlerted } from '@/utilities/orderRequestAlerts'

// ── Orders Hub ingestion ──
//
// Owns every intake side effect for the Orders Hub on an authenticated
// terminal session: the unified 60s poller, the real-time push listeners
// (Pusher/LAN), auto-fire-to-kitchen, and the audio/toast alerts.
//
// This is the AUTHENTICATED-session half of Orders Hub intake. The
// tenant-level, always-on half — boot hydration, Pusher→emitter translation,
// and the always-on QR/delivery alert sound — lives in App.vue and runs
// regardless of auth (so QR alerts sound on the login screen). This composable
// is intended to be called EXACTLY ONCE, from MainLayout's setup: MainLayout
// is only reached after user login (`/main` is `auth: 'user'`), so its
// mount/unmount lifecycle is the natural on/off switch for these side effects.
export function useOrdersHubIngestion() {
  const store = useAppStore()
  const { t } = useI18n()
  const { toast } = useToast()
  const { orderTicketsVisible } = useOrdersHub()

  const orderTicketAudio = new Audio('/audio/alert1.mp3')
  function playOrderTicketSound() {
    if (OrderTicket.getSettings()?.sound_enabled !== false) {
      orderTicketAudio.play().catch(() => {})
    }
  }

  // How long a backup terminal waits, per rank, before firing a ticket the
  // designated terminal hasn't claimed — long enough for the primary's
  // fireToKitchen (status→preparing + kitchen-ticket sync) to propagate.
  const BACKUP_AUTO_FIRE_DELAY_MS = 2500

  async function autoFireIfNeeded(ticket: OrderTicket) {
    // needsKitchenFire, not a status equality check: a platform status update
    // can move a ticket to `preparing` before any terminal fired it (no POS
    // online at ingest), and gating on `accepted` left those orders unfired
    // forever — the kitchen never heard about them.
    if (!ticket.needsKitchenFire()) return
    const settings = OrderTicket.getSettings()
    if (settings?.require_staff_confirmation) return

    // Single-firer election: only the designated terminal auto-fires.
    // rank 0 (lowest-id fire-capable terminal) fires immediately; backups wait a
    // rank-scaled delay and re-check, so the happy path never duplicates while an
    // offline primary still self-heals to the next terminal. fireToKitchen has
    // its own kitchen-ticket dedup guard as the final backstop.
    const rank = OrderTicket.autoFireRank()
    if (rank === null) return

    if (rank > 0) {
      await new Promise(resolve => setTimeout(resolve, rank * BACKUP_AUTO_FIRE_DELAY_MS))
      const fresh = OrderTicket.find(ticket.hash)
      // Same predicate as the entry guard: the primary's fire stamps a callout,
      // so a backup re-checking it sees the claim and stands down.
      if (!fresh || !fresh.needsKitchenFire()) return
    }

    await ticket.fireToKitchen()
  }

  /**
   * Self-healing pass for tickets nobody fired. Covers the terminal-was-offline
   * case: the platform advances an order's status on its own, so by the time a
   * POS comes online the ticket is neither new nor `accepted`, and no event-
   * driven path would ever fire it.
   */
  async function sweepUnfiredTickets() {
    const unfired = OrderTicket.all().filter(ticket => ticket.needsKitchenFire())
    for (const ticket of unfired) await autoFireIfNeeded(ticket)
  }

  async function onOrderTicketReceived(data: IOrderTicket) {
    // An alert must never outrun the surface it points at: assert the hub-rail
    // flag here instead of trusting every upstream push path to have set it.
    Store.set('ordersHubActive', true)
    playOrderTicketSound()

    // Only insert when the ticket is genuinely new to this terminal. The
    // 'orderTicket' event announces a CREATION (API webhook ingest) and is
    // by definition the oldest snapshot of the ticket — LAN sync or the
    // polling snapshot may have delivered a newer state first (e.g. already
    // 'preparing' after another terminal auto-fired). A blind insert would
    // regress that state and re-trigger auto-fire, duplicating KDS tickets
    // and kitchen slips.
    if (!OrderTicket.find(data.hash)) {
      OrderTicket.insert({ data: [data] })
    }

    toast(
      t('order_ticket.new_ticket_alert') + ` #${data.callout_code || data.external_code || 'TICKET'}`,
      'warning',
      { duration: 5000 }
    )

    const orderTicket = OrderTicket.find(data.hash)
    if (orderTicket) {
      await autoFireIfNeeded(orderTicket)
    }
  }

  async function onOrderTicketCancelled(data: IOrderTicket) {
    // A cancel can be the FIRST hub event this session sees (its creation push
    // may have been dropped in an offline window) — surface the hub for it too.
    Store.set('ordersHubActive', true)
    playOrderTicketSound()
    toast(
      t('order_ticket.ticket_cancelled_alert') + ` #${data.callout_code || data.external_code || 'TICKET'}`,
      'error',
      { duration: 5000 }
    )

    OrderTicket.insert({ data: [data] })
    const ticket = OrderTicket.find(data.hash)
    if (ticket) {
      const result = await ticket.handleCancellation()
      if (result.stornoFailed) {
        showAlert(
          t('order_ticket.storno_failed_title'),
          t('order_ticket.storno_failed_message', {
            code: data.callout_code || data.external_code || 'TICKET'
          })
        )
      }
    }
  }

  function onOrderTicketStatusUpdate(data: IOrderTicket) {
    // Merge (not blind upsert): a status event may predate a local completion
    // PATCH and would otherwise regress status / wipe order_hash / resurrect a
    // cancelled ticket.
    OrderTicket.mergeServerRecord(data)
  }

  // ── Real-time push listeners (Pusher/LAN) ──
  //
  // `orderTicketsVisible` is reactive (depends on configured delivery
  // platforms + module flags), so we install/uninstall listeners through a
  // watcher rather than a one-shot synchronous gate. This means a tenant
  // who configures their first delivery-platform Client mid-session
  // immediately gets audio alerts, polling, and auto-fire — no app reload
  // required. These are independent of polling.
  let orderTicketListenersActive = false

  function activateOrderTicketListeners() {
    if (orderTicketListenersActive) return
    orderTicketListenersActive = true

    emitter.on('orderTicketReceived', onOrderTicketReceived)
    emitter.on('orderTicketCancelled', onOrderTicketCancelled)
    emitter.on('orderTicketStatusUpdate', onOrderTicketStatusUpdate)
  }

  function deactivateOrderTicketListeners() {
    if (!orderTicketListenersActive) return
    orderTicketListenersActive = false

    emitter.off('orderTicketReceived', onOrderTicketReceived)
    emitter.off('orderTicketCancelled', onOrderTicketCancelled)
    emitter.off('orderTicketStatusUpdate', onOrderTicketStatusUpdate)
  }

  watch(orderTicketsVisible, (visible) => {
    if (visible) activateOrderTicketListeners()
    else deactivateOrderTicketListeners()
  }, { immediate: true })

  // ── Orders Hub: single unified poller ──
  //
  // One 60s poller replaces the former dual pollers (QR-request poller +
  // order-ticket poller). It hits the tenant-scoped `GET /orders-hub` endpoint
  // via OrdersHubService.fetchSnapshot(), which hydrates both the OrderTicket
  // and OrderRequest stores (and GCs stale requests), returning only the
  // newly-discovered items so we can raise alerts.
  //
  // Activation is tied to the MainLayout (authenticated terminal session)
  // lifecycle via this composable's setup/teardown.
  let ordersHubInterval: ReturnType<typeof setInterval> | null = null

  async function pollOrdersHub() {
    const { newTicketHashes, newRequestIds } = await OrdersHubService.fetchSnapshot()

    if (newTicketHashes.length) {
      // The snapshot delta also delivers terminal-state tickets (rejected /
      // completed while this terminal was away) so the store converges —
      // but only ACTIVE newly-discovered tickets warrant an alert.
      const tickets = newTicketHashes
        .map(h => OrderTicket.find(h))
        .filter((ticket): ticket is OrderTicket => !!ticket && ticket.isActive())
      if (tickets.length) {
        const codes = tickets.map(ticket => `#${ticket.callout_code || ticket.external_code || 'TICKET'}`).join(', ')
        toast(t('order_ticket.new_ticket_alert') + ` ${codes}`, 'warning', { duration: 5000 })
        playOrderTicketSound()
        for (const ticket of tickets) await autoFireIfNeeded(ticket)
      }
    }

    // Sweep EVERY ticket, not just newly-discovered ones. Auto-fire used to
    // hang off "new to this terminal", but boot hydration (/api/load) inserts
    // tickets before the first snapshot runs, so a ticket that arrived while no
    // terminal was online was already known by the time anything could fire it
    // — it sat in `preparing` (the platform's own status update) with no callout
    // and no kitchen ticket, forever. needsKitchenFire() makes this cheap: it
    // rejects everything already fired, and the rank election still decides who
    // actually fires.
    await sweepUnfiredTickets()

    // Route through the shared ledger: a request the real-time push already
    // alerted on is in the store (so it's not in newRequestIds), but the
    // reverse race — poll discovers it first, push arrives later — is dedup'd
    // by claiming the id here so the delayed push stays silent.
    const freshRequestIds = newRequestIds.filter(id => markOrderRequestAlerted(id))
    if (freshRequestIds.length) {
      const requests = OrderRequest.all().filter(r => freshRequestIds.includes(r.id))
      const tables = requests.map(r => r.getTableName()).join(', ')
      toast(t('orders_hub.new_order_alert') + ` ${tables}`, 'warning', { duration: 5000 })
      emitter.emit('ordersHubAlert')
    }
  }

  function startOrdersHubPolling() {
    if (ordersHubInterval) return
    // Initial fetch + 1min interval, started at MainLayout setup (session lifecycle).
    pollOrdersHub()
    ordersHubInterval = setInterval(pollOrdersHub, 1 * 60 * 1000)
  }

  // Only tenants for whom the Orders Hub is relevant poll the backend — the
  // majority (no delivery, no QR, no tickets) never hit /orders-hub at all,
  // which is the point of this gate. `ordersHubActive` flips true the moment a
  // first ticket/request arrives (Pusher push or manual entry); the watcher then
  // starts the poller mid-session with no reload. It never flips back to false,
  // so no teardown path is needed.
  if (store.ordersHubActive) startOrdersHubPolling()
  watch(() => store.ordersHubActive, (active) => {
    if (active) startOrdersHubPolling()
  })

  // Single online-reconnect handler — fetch once on offline → online transition.
  watch(() => store.isOnline, (online, wasOnline) => {
    if (online && wasOnline === false && store.ordersHubActive) pollOrdersHub()
  })

  onUnmounted(() => {
    deactivateOrderTicketListeners()
    if (ordersHubInterval) {
      clearInterval(ordersHubInterval)
      ordersHubInterval = null
    }
  })
}
